// Precondition: Shadow DOM available
// Postcondition: Renders portfolio analytics panel in compact or normal mode
// Invariant: Data format matches analytics-worker.js output

import { icon } from "../../../shared/icons.js";
import { fetchSubmissions, fetchHoldings } from "../../lib/financeapis.js";
import { createLogger } from "../../lib/logger.js";
import {
  parseEdgarSubmissions,
  getQuarter,
  getPreviousQuarter,
  formatQuarterLabel,
} from "../../lib/parsers.js";
import { Filer } from "../../models/filer.js";
import type {
  HoldingRaw,
  HoldingsProvenance,
} from "../../../shared/api-types.js";

const log = createLogger("ft-analytics-panel");

interface AnalyticsMetrics {
  holdingsCount: number;
  top10Weight: number;
  HHI: number;
  effectivePositions: number;
  turnoverEstimate: number;
  netDeltaValue: number;
  grossDeltaValue: number;
}

interface AnalyticsSignal {
  type: "NewPosition" | "ExitedPosition" | "MajorChange";
  cusip: string;
  name: string;
  weight?: number;
  prevWeight?: number;
  deltaWeight?: number;
}

interface AnalyticsResult {
  type: "result";
  totals: {
    currentTotalValue: number;
    previousTotalValue: number;
  };
  metrics: AnalyticsMetrics;
  signals: AnalyticsSignal[];
  topConviction: Array<{
    cusip: string;
    name: string;
    score: number;
    weight: number;
  }>;
}

type PanelMode = "compact" | "normal";
type PanelState = "idle" | "loading" | "error" | "empty";

class AnalyticsPanel extends HTMLElement {
  private data: AnalyticsResult | null = null;
  private errorMessage: string | null = null;
  private worker: Worker | null = null;
  private pending = false;
  private provenance: HoldingsProvenance | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.initWorker();
  }

  private initWorker(): void {
    const workerUrl = this.getAttribute("worker-url") || "/bundles/workers.js";
    try {
      this.worker = new Worker(workerUrl);
      this.worker.onmessage = (e) => {
        if (e.data.type === "result") {
          log.debug("worker result received");
          this.setData(e.data);
        }
      };
      this.worker.onerror = (err) => {
        log.error("worker computation failed", { error: err.message });
        this.setError("Analytics computation failed");
      };
      log.log("worker initialized");
    } catch (err) {
      log.warn("worker unavailable", { error: err });
    }
  }

  // Precondition: cik, q1, q2 attributes are set
  // Postcondition: Fetches holdings and sends to worker for computation
  private async loadHoldings(): Promise<void> {
    const cik = this.getAttribute("cik");
    const q1 = this.getAttribute("q1");
    const q2 = this.getAttribute("q2");

    if (!cik || !q1 || !q2) return;

    log.log("loadHoldings", { cik, q1, q2 });
    this.state = "loading";

    const result = await fetchHoldings(cik, q1, q2);

    if (!result.ok) {
      log.error("loadHoldings failed", { cik, q1, q2, error: result.error });
      this.setError("Unable to load holdings data");
      return;
    }

    const holdings = result.data.holdings || [];
    const provenance = result.data.provenance || { cik, q1, q2 };
    log.debug("loadHoldings success", { count: holdings.length });

    if (holdings.length > 0) {
      this.setHoldings(holdings, provenance);
    } else {
      this.setEmpty("No holdings data available");
    }
  }

  static get observedAttributes(): string[] {
    return ["mode", "state", "tier", "cik", "q1", "q2"];
  }

  // Precondition: None
  // Postcondition: Returns current mode, defaults to "normal"
  get mode(): PanelMode {
    const attr = this.getAttribute("mode");
    return attr === "compact" ? "compact" : "normal";
  }

  // Precondition: Valid mode string
  // Postcondition: Mode attribute set
  set mode(value: PanelMode) {
    this.setAttribute("mode", value);
  }

  // Precondition: None
  // Postcondition: Returns current state, defaults to "idle"
  get state(): PanelState {
    return (this.getAttribute("state") as PanelState) || "idle";
  }

  // Precondition: Valid state string
  // Postcondition: State attribute set
  set state(value: PanelState) {
    this.setAttribute("state", value);
  }

  // Precondition: Element connected to DOM
  // Postcondition: Component rendered, loads data if cik present
  connectedCallback(): void {
    log.debug("connected", {
      mode: this.mode,
      tier: this.getAttribute("tier"),
    });
    this.render();

    const cik = this.getAttribute("cik");
    if (!cik) return;

    const q1 = this.getAttribute("q1");
    const q2 = this.getAttribute("q2");

    if (q1 && q2) {
      // Quarter page: have all attributes, fetch holdings directly
      // Use microtask to batch with any attributeChangedCallback calls during element upgrade
      if (!this.pending) {
        this.pending = true;
        queueMicrotask(() => {
          this.pending = false;
          this.loadHoldings();
        });
      }
    } else {
      // Company page: need to get q1/q2 from submissions first
      // Call immediately - loadFromCik sets q1/q2 which triggers attributeChangedCallback
      this.loadFromCik(cik);
    }

    // Listen for quarter selection changes from ft-company-quarter
    document.addEventListener("quarter-change", this.handleQuarterChange);
  }

  // Precondition: quarter-change event with detail.quarter
  // Postcondition: Updates q1/q2 attributes, triggers reload
  private handleQuarterChange = (e: Event): void => {
    const { quarter } = (e as CustomEvent).detail;
    if (!quarter) return;

    log.log("handleQuarterChange", { quarter });
    const q1 = getPreviousQuarter(quarter);
    this.setAttribute("q1", q1);
    this.setAttribute("q2", quarter);
  };

  // Precondition: cik is set, q1/q2 are not
  // Postcondition: Fetches submissions to get q1/q2, then loads holdings
  private async loadFromCik(cik: string): Promise<void> {
    log.log("loadFromCik", { cik });
    this.state = "loading";

    const result = await fetchSubmissions(cik);

    if (!result.ok) {
      log.error("loadFromCik failed", { cik, error: result.error });
      this.setError("Unable to load company data");
      return;
    }

    const submissions = parseEdgarSubmissions(result.data);
    if (!submissions.length) {
      this.setEmpty("No 13F filings found");
      return;
    }

    // Get latest quarter from submissions
    const latest = submissions[0];
    const q2 = getQuarter(latest.reportDate || latest.filingDate);
    const q1 = getPreviousQuarter(q2);

    log.debug("loadFromCik quarters", { q1, q2 });
    this.setAttribute("q1", q1);
    this.setAttribute("q2", q2);
    // attributeChangedCallback will trigger loadHoldings via microtask
  }

  // Precondition: Element disconnecting from DOM
  // Postcondition: Worker terminated, event listeners removed
  disconnectedCallback(): void {
    log.debug("disconnected");
    this.worker?.terminate();
    this.worker = null;
    document.removeEventListener("quarter-change", this.handleQuarterChange);
  }

  // Precondition: Attribute changed
  // Postcondition: Component re-renders, batches cik/q1/q2 changes via microtask
  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue || !this.shadowRoot) return;

    if (name === "mode" || name === "state" || name === "tier") {
      this.render();
      return;
    }

    // Batch cik/q1/q2 attribute changes via microtask.
    // Sequential setAttribute calls fire attributeChangedCallback 3x.
    // Microtask defers execution until after all sync code completes,
    // coalescing multiple attribute changes into a single loadHoldings call.
    if (name === "cik" || name === "q1" || name === "q2") {
      if (this.pending) return;
      this.pending = true;
      queueMicrotask(() => {
        this.pending = false;
        const cik = this.getAttribute("cik");
        const q1 = this.getAttribute("q1");
        const q2 = this.getAttribute("q2");
        if (cik && q1 && q2) {
          this.loadHoldings();
        }
      });
    }
  }

  // Precondition: Shadow root exists
  // Postcondition: Component DOM rendered with styles
  private render(): void {
    if (!this.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const quarterLabel = formatQuarterLabel(this.getAttribute("q2"));
    const wrapper = document.createElement("div");
    wrapper.className = "panel-wrapper";
    wrapper.innerHTML = `
      ${quarterLabel ? `<span class="component-badge">${quarterLabel}</span>` : ""}
      ${this.getOverlayTemplate()}
      <div class="panel ${this.mode}">
        ${this.getTemplate()}
      </div>
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(wrapper);
  }

  // Precondition: shadowRoot exists
  // Postcondition: Updates badge text with filer name + quarter
  private updateBadge(): void {
    const badgeEl = this.shadowRoot?.querySelector(".component-badge");
    if (!badgeEl) return;

    const quarterLabel = formatQuarterLabel(this.getAttribute("q2"));
    const filerName = this.provenance?.filerName;
    const badgeText =
      filerName && quarterLabel ? `${filerName} ${quarterLabel}` : quarterLabel;

    if (badgeText) {
      badgeEl.textContent = badgeText;
    }
  }

  private getOverlayTemplate(): string {
    if (this.state === "idle") return "";

    let content: string;
    if (this.state === "loading") {
      content = '<ft-spinner text="Calculating analytics..."></ft-spinner>';
    } else if (this.state === "error") {
      content = `${icon("alert-circle", 16)}${this.errorMessage || "Unable to load analytics"}`;
    } else {
      content = "No analytics data available";
    }

    return `<div class="overlay${this.state === "error" ? " error" : ""}">${content}</div>`;
  }

  private getEmptyData(): AnalyticsResult {
    return {
      type: "result",
      totals: { currentTotalValue: 0, previousTotalValue: 0 },
      metrics: {
        holdingsCount: 0,
        top10Weight: 0,
        HHI: 0,
        effectivePositions: 0,
        turnoverEstimate: 0,
        netDeltaValue: 0,
        grossDeltaValue: 0,
      },
      signals: [],
      topConviction: [],
    };
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        margin-bottom: 1.5em;
        --_font-display: "DM Serif Display", Georgia, serif;
        --_font-body: "Space Grotesk", system-ui, sans-serif;
        --_panel: #111111;
        --_surface: #0a0a0a;
        --_border: #161616;
        --_border-accent: #1f1f1f;
        --_text: #f0f0f0;
        --_text-secondary: #a0a0a0;
        --_text-dim: #525252;
        --_text-faint: #3a3a3a;
        --_accent: #c9a84c;
        --_green: #22c55e;
        --_red: #ef4444;
        --_green-bg: rgba(34, 197, 94, 0.12);
        --_red-bg: rgba(239, 68, 68, 0.12);
        --_warning: #d97706;
        --_warning-bg: rgba(217, 119, 6, 0.12);
      }

      @media (prefers-color-scheme: light) {
        :host {
          --_panel: #ffffff;
          --_surface: #f5f5f4;
          --_border: #e2e4e8;
          --_border-accent: #d1d5db;
          --_text: #1c1c1e;
          --_text-secondary: #666666;
          --_text-dim: #888888;
          --_text-faint: #bbbbbb;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          transition-duration: 0.01ms !important;
        }
      }

      * { box-sizing: border-box; margin: 0; padding: 0; }

      .panel-wrapper { position: relative; overflow: visible; }

      .overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5em;
        background: var(--_panel);
        z-index: 10;
        font: 400 0.8125rem/1.5 var(--_font-body);
        color: var(--_text-dim);
        border-radius: 6px;
        animation: overlayIn 200ms ease-out;
      }

      .overlay svg { width: 1rem; height: 1rem; flex-shrink: 0; }
      .overlay.error { color: var(--_red); }

      @keyframes overlayIn { from { opacity: 0; } }

      @media (prefers-reduced-motion: reduce) {
        .overlay { animation: none; }
      }

      .panel {
        background: var(--_panel);
        border: 1px solid var(--_border);
        border-radius: 6px;
        overflow: hidden;
      }

      .panel.hidden { display: none; }

      .positive { color: var(--_green); }
      .negative { color: var(--_red); }
      .neutral { color: var(--_text-dim); }

      .component-badge {
        margin-bottom: -1px;
        font: 600 0.6875rem/1 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--_text-dim);
        background: var(--_surface);
        padding: 0.3em 0.5em;
        border-radius: 3px;
        border: 1px solid var(--_border);
      }

      /* ========================================
         NORMAL MODE (Hero + Compact Grid)
         ======================================== */
      .panel.normal .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1px;
        background: var(--_border);
      }

      .hero {
        background: var(--_panel);
        padding: 1.125em 1em;
        grid-row: span 2;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .hero-label {
        font: 500 0.5rem/1 var(--_font-body);
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--_text-dim);
        margin-bottom: 0.25em;
      }

      .hero-value {
        font: 700 clamp(1.5rem, 3vw, 2.25rem)/1 var(--_font-display);
        color: var(--_text);
        letter-spacing: -0.03em;
        font-variant-numeric: tabular-nums;
      }

      .hero-delta {
        font: 400 0.6875rem/1 var(--_font-body);
        margin-top: 0.375em;
        font-variant-numeric: tabular-nums;
      }

      .hero-prev {
        font: 400 0.5625rem/1 var(--_font-body);
        color: var(--_text-faint);
        margin-top: 0.125em;
      }

      .hero-mini-row {
        display: flex;
        gap: 1.5em;
        padding-top: 0.75em;
        margin-top: 0.75em;
        border-top: 1px solid var(--_border);
      }

      .mini-stat { flex: 1; }

      .mini-value {
        font: 600 1rem/1 var(--_font-body);
        color: var(--_text);
        font-variant-numeric: tabular-nums;
      }

      .mini-label {
        font: 400 0.5rem/1.3 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--_text-dim);
        margin-top: 0.25em;
      }

      .compact-cell {
        background: var(--_panel);
        padding: 0.75em 1em;
      }

      .compact-main {
        display: flex;
        align-items: baseline;
        gap: 0.5em;
      }

      .compact-value {
        font: 600 1.125rem/1 var(--_font-body);
        color: var(--_text);
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.01em;
      }

      .compact-right {
        margin-left: auto;
        text-align: right;
      }

      .compact-label {
        font: 500 0.46875rem/1 var(--_font-body);
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--_text-dim);
      }

      .compact-delta {
        font: 400 0.5rem/1 var(--_font-body);
        margin-top: 0.125em;
        font-variant-numeric: tabular-nums;
        color: var(--_text-secondary);
      }

      .compact-signals {
        display: flex;
        gap: 0.25em;
        margin-top: 0.375em;
      }

      .chip {
        font: 600 0.5625rem/1 var(--_font-body);
        padding: 0.15em 0.4em;
        border-radius: 2px;
      }

      .chip.new {
        color: var(--_green);
        background: var(--_green-bg);
      }

      .chip.exited {
        color: var(--_red);
        background: var(--_red-bg);
      }

      .chip.change {
        color: var(--_warning);
        background: var(--_warning-bg);
      }

      /* ========================================
         COMPACT MODE (Horizontal Rows)
         ======================================== */
      .panel.compact .stack {
        display: flex;
        flex-direction: column;
      }

      .panel.compact .row {
        display: grid;
        grid-template-columns: 100px 1fr;
        align-items: center;
        padding: 1em 1.25em;
        gap: 1.25em;
        border-bottom: 1px solid var(--_border);
      }

      .panel.compact .row:last-child { border-bottom: none; }

      .row-label {
        font: 500 0.625rem/1 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--_text-dim);
      }

      .row-content {
        display: flex;
        align-items: center;
        gap: 1.5em;
        flex-wrap: wrap;
      }

      .inline-stat {
        display: flex;
        align-items: baseline;
        gap: 0.375em;
      }

      .inline-value {
        font: 600 1.375rem/1 var(--_font-display);
        color: var(--_text);
        letter-spacing: -0.02em;
      }

      .inline-label {
        font: 400 0.6875rem/1 var(--_font-body);
        color: var(--_text-dim);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      .inline-change {
        font: 500 0.8125rem/1 var(--_font-body);
        margin-left: 0.375em;
      }

      .signals-inline {
        display: flex;
        gap: 0.375em;
        margin-left: auto;
      }

      .signal-tag {
        display: inline-flex;
        align-items: center;
        gap: 0.25em;
        font: 500 0.6875rem/1 var(--_font-body);
        text-transform: uppercase;
        letter-spacing: 0.03em;
        padding: 0.375em 0.625em;
        border-radius: 3px;
        border: 1px solid var(--_border);
      }

      .signal-tag.new {
        color: var(--_green);
        border-color: var(--_green);
        background: var(--_green-bg);
      }

      .signal-tag.exited {
        color: var(--_red);
        border-color: var(--_red);
        background: var(--_red-bg);
      }

      .signal-tag.change {
        color: var(--_warning);
        border-color: var(--_warning);
        background: var(--_warning-bg);
      }

      /* Concentration colors */
      .concentration-very-high { color: var(--_red); }
      .concentration-high { color: var(--_warning); }
      .concentration-moderate { color: var(--_text); }
      .concentration-diversified { color: var(--_green); }

      /* Responsive */
      @media (max-width: 700px) {
        .panel.normal .grid {
          grid-template-columns: 1fr;
        }

        .hero { grid-row: span 1; }
        .hero-value { font-size: 1.5rem; }

        .panel.compact .row {
          grid-template-columns: 1fr;
          gap: 0.625em;
        }

        .row-content { gap: 1em; }
        .inline-value { font-size: 1.125rem; }

        .signals-inline {
          margin-left: 0;
          width: 100%;
        }
      }
    `;
  }

  private getTemplate(): string {
    const displayData = this.data ?? this.getEmptyData();

    return this.mode === "compact"
      ? this.getCompactTemplate(displayData)
      : this.getNormalTemplate(displayData);
  }

  // Precondition: data parameter is not null
  // Postcondition: Returns HTML for normal (hero + compact cells) layout
  private getNormalTemplate(data: AnalyticsResult): string {
    const { totals, metrics, signals } = data;
    const concentrationLabel = this.getConcentrationLabel(metrics.HHI);
    const qoqChange = this.calculateQoQChange(
      totals.currentTotalValue,
      totals.previousTotalValue,
    );
    const absoluteChange = totals.currentTotalValue - totals.previousTotalValue;

    const q1 = this.getAttribute("q1") || "";
    const q2 = this.getAttribute("q2") || "";
    const prevQuarter = q1 < q2 ? q1 : q2;
    const prevLabel = formatQuarterLabel(prevQuarter);

    const newCount = signals.filter((s) => s.type === "NewPosition").length;
    const exitedCount = signals.filter(
      (s) => s.type === "ExitedPosition",
    ).length;
    const changeCount = signals.filter((s) => s.type === "MajorChange").length;
    const holdingsChange = newCount - exitedCount;
    const holdingsChangeStr =
      holdingsChange > 0 ? `+${holdingsChange}` : `${holdingsChange}`;

    const hasSignals = newCount + exitedCount + changeCount > 0;

    return `
      <div class="grid">
        <div class="hero">
          <div class="hero-label">Portfolio Value</div>
          <div class="hero-value">${this.formatCurrency(totals.currentTotalValue)}</div>
          <div class="hero-delta ${qoqChange >= 0 ? "positive" : "negative"}">${this.formatCurrency(absoluteChange, true)} (${qoqChange >= 0 ? "+" : ""}${qoqChange.toFixed(1)}%)</div>
          ${prevLabel ? `<div class="hero-prev">${prevLabel}: ${this.formatCurrency(totals.previousTotalValue)}</div>` : ""}
          <div class="hero-mini-row">
            <div class="mini-stat">
              <div class="mini-value ${metrics.netDeltaValue >= 0 ? "positive" : "negative"}">${this.formatCurrency(metrics.netDeltaValue, true)}</div>
              <div class="mini-label">Net Delta</div>
            </div>
            <div class="mini-stat">
              <div class="mini-value">${this.formatCurrency(metrics.grossDeltaValue)}</div>
              <div class="mini-label">Gross Delta</div>
            </div>
          </div>
        </div>

        <div class="compact-cell">
          <div class="compact-main">
            <div class="compact-value">${this.formatPercent(metrics.top10Weight)}</div>
            <div class="compact-right">
              <div class="compact-label">Top 10 Weight</div>
              <div class="compact-delta neutral">${concentrationLabel} · ${metrics.effectivePositions.toFixed(1)} eff pos</div>
            </div>
          </div>
        </div>

        <div class="compact-cell">
          <div class="compact-main">
            <div class="compact-value">${metrics.holdingsCount}</div>
            <div class="compact-right">
              <div class="compact-label">Holdings</div>
              <div class="compact-delta">${holdingsChangeStr} vs ${prevLabel || "prev"} · ${this.formatPercent(metrics.turnoverEstimate)} turnover</div>
            </div>
          </div>
          ${
            hasSignals
              ? `<div class="compact-signals">
            ${newCount > 0 ? `<span class="chip new">+${newCount}</span>` : ""}
            ${exitedCount > 0 ? `<span class="chip exited">-${exitedCount}</span>` : ""}
            ${changeCount > 0 ? `<span class="chip change">~${changeCount}</span>` : ""}
          </div>`
              : ""
          }
        </div>
      </div>
    `;
  }

  // Precondition: data parameter is not null
  // Postcondition: Returns HTML for compact (horizontal rows) layout
  private getCompactTemplate(data: AnalyticsResult): string {
    const { totals, metrics, signals } = data;
    const concentrationClass = this.getConcentrationClass(metrics.HHI);
    const concentrationLabel = this.getConcentrationLabel(metrics.HHI);
    const qoqChange = this.calculateQoQChange(
      totals.currentTotalValue,
      totals.previousTotalValue,
    );

    const newCount = signals.filter((s) => s.type === "NewPosition").length;
    const exitedCount = signals.filter(
      (s) => s.type === "ExitedPosition",
    ).length;
    const changeCount = signals.filter((s) => s.type === "MajorChange").length;

    return `
      <div class="stack">
        <div class="row">
          <div class="row-label">Value</div>
          <div class="row-content">
            <div class="inline-stat">
              <span class="inline-value">${this.formatCurrency(totals.currentTotalValue)}</span>
              <span class="inline-label">Portfolio</span>
              <span class="inline-change ${qoqChange >= 0 ? "positive" : "negative"}">${qoqChange >= 0 ? "+" : ""}${qoqChange.toFixed(1)}%</span>
            </div>
            <div class="inline-stat">
              <span class="inline-value ${metrics.netDeltaValue >= 0 ? "positive" : "negative"}">${this.formatCurrency(metrics.netDeltaValue, true)}</span>
              <span class="inline-label">Net</span>
            </div>
            <div class="inline-stat">
              <span class="inline-value">${this.formatCurrency(metrics.grossDeltaValue)}</span>
              <span class="inline-label">Gross</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="row-label">Structure</div>
          <div class="row-content">
            <div class="inline-stat">
              <span class="inline-value">${metrics.holdingsCount}</span>
              <span class="inline-label">Holdings</span>
            </div>
            <div class="inline-stat">
              <span class="inline-value">${this.formatPercent(metrics.top10Weight)}</span>
              <span class="inline-label">Top 10</span>
            </div>
            <div class="inline-stat">
              <span class="inline-value ${concentrationClass}">${concentrationLabel}</span>
              <span class="inline-label">Concentration</span>
            </div>
            <div class="inline-stat">
              <span class="inline-value">${metrics.effectivePositions.toFixed(1)}</span>
              <span class="inline-label">Effective</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="row-label">Activity</div>
          <div class="row-content">
            <div class="inline-stat">
              <span class="inline-value">${this.formatPercent(metrics.turnoverEstimate)}</span>
              <span class="inline-label">Turnover</span>
            </div>
            <div class="signals-inline">
              ${newCount > 0 ? `<span class="signal-tag new">+${newCount} New</span>` : ""}
              ${exitedCount > 0 ? `<span class="signal-tag exited">-${exitedCount} Exited</span>` : ""}
              ${changeCount > 0 ? `<span class="signal-tag change">${changeCount} Changes</span>` : ""}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Precondition: HHI is a number between 0 and 1
  // Postcondition: Returns CSS class for concentration level
  private getConcentrationClass(hhi: number): string {
    if (hhi >= 0.25) return "concentration-very-high";
    if (hhi >= 0.15) return "concentration-high";
    if (hhi >= 0.1) return "concentration-moderate";
    return "concentration-diversified";
  }

  // Precondition: HHI is a number between 0 and 1
  // Postcondition: Returns human-readable concentration label
  private getConcentrationLabel(hhi: number): string {
    if (hhi >= 0.25) return "V. High";
    if (hhi >= 0.15) return "High";
    if (hhi >= 0.1) return "Moderate";
    return "Diversified";
  }

  // Precondition: value is a number
  // Postcondition: Returns formatted currency string (e.g., "$2.5B")
  private formatCurrency(value: number, showSign = false): string {
    if (value === null || value === undefined || isNaN(value)) return "—";

    const absValue = Math.abs(value);
    const sign = showSign && value > 0 ? "+" : value < 0 ? "-" : "";
    const displayValue = showSign ? absValue : value;

    if (absValue >= 1e12) {
      return `${sign}$${(displayValue / 1e12).toFixed(1)}T`;
    } else if (absValue >= 1e9) {
      return `${sign}$${(displayValue / 1e9).toFixed(1)}B`;
    } else if (absValue >= 1e6) {
      return `${sign}$${(displayValue / 1e6).toFixed(1)}M`;
    } else if (absValue >= 1e3) {
      return `${sign}$${(displayValue / 1e3).toFixed(0)}K`;
    }
    return `${sign}$${displayValue.toFixed(0)}`;
  }

  // Precondition: value is a decimal (e.g., 0.723)
  // Postcondition: Returns formatted percentage string (e.g., "72%")
  private formatPercent(value: number): string {
    if (value === null || value === undefined || isNaN(value)) return "—";
    return `${Math.round(value * 100)}%`;
  }

  // Precondition: current and previous are numbers
  // Postcondition: Returns percentage change
  private calculateQoQChange(current: number, previous: number): number {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }

  // Public API

  // Precondition: holdings array with value/delta fields, provenance with filing accessions
  // Postcondition: Shows loading, sends to worker for computation
  setHoldings(holdings: HoldingRaw[], provenance?: HoldingsProvenance): void {
    if (!this.worker || holdings.length === 0) {
      log.warn("setHoldings skipped", {
        hasWorker: !!this.worker,
        count: holdings.length,
      });
      return;
    }

    if (provenance) {
      this.provenance = provenance;
      this.updateBadge();

      // Fetch filer name if not in provenance
      if (!provenance.filerName && provenance.cik) {
        this.loadFilerName(provenance.cik);
      }
    }

    log.log("setHoldings", { count: holdings.length });
    this.state = "loading";

    this.worker.postMessage({
      type: "analytics:compute",
      holdings,
      provenance,
      tier: this.getAttribute("tier") || "anonymous",
    });
  }

  // Precondition: cik is valid SEC CIK
  // Postcondition: Filer name fetched and badge updated
  private async loadFilerName(cik: string): Promise<void> {
    const filer = await Filer.find(cik);
    if (!filer || !this.provenance) return;
    this.provenance.filerName = filer.name;
    this.updateBadge();
  }

  // Precondition: None
  // Postcondition: Panel shows loading state
  setLoading(message?: string): void {
    this.state = "loading";
    if (message) {
      const spinner = this.shadowRoot?.querySelector("ft-spinner");
      if (spinner) spinner.setAttribute("text", message);
    }
  }

  // Precondition: result matches AnalyticsResult interface
  // Postcondition: Panel re-renders with new data, state set to idle
  setData(result: AnalyticsResult): void {
    log.debug("setData", {
      holdings: result.metrics.holdingsCount,
      signals: result.signals.length,
    });
    this.data = result;
    this.errorMessage = null;
    this.state = "idle";
  }

  // Precondition: None
  // Postcondition: Returns current data or null
  getData(): AnalyticsResult | null {
    return this.data;
  }

  // Precondition: None
  // Postcondition: Panel shows error state
  setError(message?: string): void {
    const errorMessage = message || "Unknown error";
    log.error("setError", { message: errorMessage });
    this.errorMessage = message || null;
    this.state = "error";
  }

  // Precondition: None
  // Postcondition: Panel shows empty state
  setEmpty(message?: string): void {
    this.data = null;
    if (message) this.errorMessage = message;
    this.state = "empty";
  }

  // Precondition: None
  // Postcondition: Error cleared, returns to idle or empty based on data
  clearError(): void {
    this.errorMessage = null;
    this.state = this.data ? "idle" : "empty";
  }

  // Precondition: None
  // Postcondition: Panel hidden
  hide(): void {
    const panel = this.shadowRoot?.querySelector(".panel");
    panel?.classList.add("hidden");
  }

  // Precondition: None
  // Postcondition: Panel visible
  show(): void {
    const panel = this.shadowRoot?.querySelector(".panel");
    panel?.classList.remove("hidden");
  }

  // Precondition: None
  // Postcondition: Switches to compact mode
  setCompact(): void {
    this.mode = "compact";
  }

  // Precondition: None
  // Postcondition: Switches to normal mode
  setNormal(): void {
    this.mode = "normal";
  }
}

// Guard against duplicate registration
if (!customElements.get("ft-analytics-panel")) {
  customElements.define("ft-analytics-panel", AnalyticsPanel);
}
